import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const fmtUSD = (n: number) => isFinite(n) ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n) : "—";
const fmtUSD2 = (n: number) => isFinite(n) ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(n) : "—";
const fmtNum = (n: number) => new Intl.NumberFormat("en-US").format(Math.round(n));

// 2026-05-31: dropped 'bead_avg' source (cross-project averaging removed);
// added 'project_rate' for per-project bead_budget_line lookups.
type Source = "override" | "project_rate" | "catalog" | "missing";

interface LineItem {
  category: string;
  sku: string;
  qty: number;
  unit: string;
  unit_price: number;
  cost: number;
  price_source: Source;
  vendor?: string | null;
  n_projects?: number;
  notes?: string;
}

interface DensityComparison {
  plan: { miles: number; passings: number; per_mile: number };
  bead_baseline: {
    n_projects: number; total_bsls: number; total_miles: number;
    bsls_per_mile: number; avg_labor_per_bsl: number; avg_labor_per_mile: number;
  };
  ratio: number;
  note: string;
}
interface PricingModel {
  plan: { id: string; name: string; plant_id: string; engineered_capex: number; total_passings: number };
  cost_catalog: { id: string; name: string; region_mult: number; notes: string | null; updated_at: string };
  pricing_method: { order: string[]; split_ratio: string; active_pon_ports: number; sliders?: { aerial_pct: number; take_rate_pct: number } };
  line_items: LineItem[];
  adder_items: LineItem[];
  totals: { direct: number; adders: number; grand_total: number; by_source: Record<Source, number> };
  density_comparison: DensityComparison;
}

const SOURCE_BADGE: Record<Source, { label: string; bg: string; fg: string }> = {
  override:     { label: "Override",     bg: "#dbeafe", fg: "#1e40af" },
  project_rate: { label: "Project rate", bg: "#dcfce7", fg: "#166534" },
  catalog:      { label: "Rate Card",    bg: "#fef3c7", fg: "#92400e" },
  missing:      { label: "No price",     bg: "#fee2e2", fg: "#991b1b" },
};

export default function PricingModelPage() {
  const [params] = useSearchParams();
  const planId = params.get("planId") || "";
  const [data, setData] = useState<PricingModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!planId) { setError("Missing planId"); return; }
    document.title = "Pricing Model";
    api.get(`/territory/plans/${planId}/pricing-model${(() => {
        const a = params.get('aerialPct'); const t = params.get('takeRate');
        const qs: string[] = [];
        if (a !== null) qs.push(`aerialPct=${encodeURIComponent(a)}`);
        if (t !== null) qs.push(`takeRate=${encodeURIComponent(t)}`);
        return qs.length ? `?${qs.join('&')}` : '';
      })()}`)
      .then((d: PricingModel) => { setData(d); document.title = `Pricing Model – ${d.plan.name}`; })
      .catch((e: Error) => setError(e.message));
  }, [planId]);

  if (error) return <div style={{ padding: 24, fontFamily: "system-ui", color: "#b91c1c" }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 24, fontFamily: "system-ui" }}>Loading pricing model…</div>;

  const byCat: Record<string, LineItem[]> = {};
  for (const l of data.line_items) (byCat[l.category] = byCat[l.category] || []).push(l);

  const costPerPassing = data.plan.total_passings > 0 ? data.totals.grand_total / data.plan.total_passings : 0;
  const variance = data.totals.grand_total - data.plan.engineered_capex;

  const sourcePct = (s: Source) => {
    const v = data.totals.by_source[s] || 0;
    const tot = data.totals.direct || 1;
    return (v / tot) * 100;
  };

  return (
    <div style={{ padding: "32px 40px", fontFamily: "system-ui, -apple-system, sans-serif", color: "#0f172a", maxWidth: 1100, margin: "0 auto", background: "#fff" }}>
      <style>{`
        @media print { body { background: #fff !important; } .no-print { display: none !important; } table { page-break-inside: auto; } tr { page-break-inside: avoid; } }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th { text-align: left; padding: 6px 8px; background: #f1f5f9; border-bottom: 2px solid #cbd5e1; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
        td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        td.right { text-align: right; font-variant-numeric: tabular-nums; }
        h1 { margin: 0 0 4px; font-size: 24px; }
        h2 { margin: 24px 0 8px; font-size: 14px; text-transform: uppercase; letter-spacing: .06em; color: #334155; border-top: 1px solid #cbd5e1; padding-top: 12px; }
        .muted { color: #64748b; font-size: 12px; }
        .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 16px 0 8px; }
        .stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; }
        .stat .label { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #64748b; }
        .stat .value { font-size: 18px; font-weight: 700; margin-top: 2px; }
        .total-row td { border-top: 2px solid #0f172a; border-bottom: 2px solid #0f172a; font-weight: 700; }
        .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
        .src-bar { display: flex; height: 12px; border-radius: 3px; overflow: hidden; margin: 6px 0 4px; }
        .src-bar > div { transition: width .2s; }
      `}</style>

      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <button onClick={() => window.print()} style={{ background: "#0f172a", color: "#fff", border: 0, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          🖨️ Print / Save PDF
        </button>
        <button onClick={() => window.close()} style={{ background: "#fff", color: "#0f172a", border: "1px solid #cbd5e1", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
          Close
        </button>
      </div>

      <h1>Pricing Model</h1>
      <div className="muted">{data.plan.name}</div>
      <div className="muted" style={{ marginTop: 4 }}>
        Source priority: <b>BEAD current-price overrides</b> → <b>BEAD-submitted weighted avg</b> → <b>cost_catalog fallback</b>.
        Split ratio {data.pricing_method.split_ratio}; {fmtNum(data.pricing_method.active_pon_ports)} active PON ports.
      </div>
      {data.pricing_method.sliders && (
        <div className="muted" style={{ marginTop: 6 }}>
          <span style={{ fontSize: 10, padding: '2px 8px', background: '#e0f2fe', color: '#075985', borderRadius: 10, marginRight: 6 }}>
            ≡ Slider state · {data.pricing_method.sliders.aerial_pct}% aerial / {100 - data.pricing_method.sliders.aerial_pct}% buried · {data.pricing_method.sliders.take_rate_pct}% take rate
          </span>
        </div>
      )}

      <div className="summary">
        <div className="stat"><div className="label">Direct cost</div><div className="value">{fmtUSD(data.totals.direct)}</div></div>
        <div className="stat"><div className="label">Adders</div><div className="value">{fmtUSD(data.totals.adders)}</div></div>
        <div className="stat"><div className="label">Grand total</div><div className="value">{fmtUSD(data.totals.grand_total)}</div></div>
        <div className="stat"><div className="label">$ / passing</div><div className="value">{fmtUSD(costPerPassing)}</div></div>
      </div>

      <div className="muted" style={{ marginTop: 8 }}>
        <b>Direct cost by price source:</b>
        <div className="src-bar">
          {(Object.keys(SOURCE_BADGE) as Source[]).map((s) => {
            const w = sourcePct(s);
            if (w < 0.1) return null;
            return <div key={s} title={`${SOURCE_BADGE[s].label} ${w.toFixed(1)}%`} style={{ background: SOURCE_BADGE[s].fg, width: `${w}%` }} />;
          })}
        </div>
        {(Object.keys(SOURCE_BADGE) as Source[]).map((s) => {
          const v = data.totals.by_source[s] || 0;
          const w = sourcePct(s);
          if (v < 1) return null;
          return (
            <span key={s} style={{ marginRight: 12, fontSize: 11 }}>
              <span className="badge" style={{ background: SOURCE_BADGE[s].bg, color: SOURCE_BADGE[s].fg }}>{SOURCE_BADGE[s].label}</span>
              {" "}{fmtUSD(v)} ({w.toFixed(1)}%)
            </span>
          );
        })}
      </div>

      <div className="muted" style={{ marginTop: 8 }}>
        Engineered capex on plan: {fmtUSD(data.plan.engineered_capex)}
        {Math.abs(variance) > 1 && (
          <span> · Variance: <b style={{ color: variance >= 0 ? "#b91c1c" : "#15803d" }}>{variance >= 0 ? "+" : "−"}{fmtUSD(Math.abs(variance))}</b> ({(variance / Math.max(1, data.plan.engineered_capex) * 100).toFixed(1)}%)</span>
        )}
        {" · "}{fmtNum(data.plan.total_passings)} passings
      </div>

      {data.density_comparison && (
        <div style={{
          marginTop: 18,
          padding: '10px 14px',
          border: '1px solid #fbbf24',
          background: '#fffbeb',
          borderRadius: 6,
          fontSize: 12,
          color: '#78350f',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            ⚠ Density vs BEAD baseline
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: '#92400e' }}>This plan</div>
              <div><b>{fmtNum(data.density_comparison.plan.miles)}</b> mi · <b>{fmtNum(data.density_comparison.plan.passings)}</b> passings</div>
              <div style={{ fontSize: 11 }}>= <b>{data.density_comparison.plan.per_mile}</b> passings/mi</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#92400e' }}>BEAD baseline ({data.density_comparison.bead_baseline.n_projects} fiber projects with mile detail)</div>
              <div><b>{fmtNum(data.density_comparison.bead_baseline.total_miles)}</b> mi · <b>{fmtNum(data.density_comparison.bead_baseline.total_bsls)}</b> BSLs</div>
              <div style={{ fontSize: 11 }}>= <b>{data.density_comparison.bead_baseline.bsls_per_mile}</b> BSLs/mi · {fmtUSD(data.density_comparison.bead_baseline.avg_labor_per_mile)}/mi labor</div>
            </div>
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.5 }}>
            {data.density_comparison.note}
          </div>
        </div>
      )}

      {Object.entries(byCat).map(([cat, items]) => {
        const subtotal = items.reduce((a, l) => a + l.cost, 0);
        return (
          <div key={cat}>
            <h2>{cat}</h2>
            <table>
              <thead>
                <tr><th>SKU</th><th>Source</th><th className="right">Qty</th><th>Unit</th><th className="right">Unit price</th><th className="right">Cost</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {items.map((l, i) => {
                  const badge = SOURCE_BADGE[l.price_source];
                  return (
                    <tr key={i} style={l.price_source === "missing" ? { background: "#fef2f2" } : {}}>
                      <td>{l.sku}</td>
                      <td>
                        <span className="badge" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>
                        {l.n_projects ? <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>n={l.n_projects} projects</div> : null}
                        {l.vendor ? <div style={{ fontSize: 10, color: "#64748b" }}>{l.vendor}</div> : null}
                      </td>
                      <td className="right">{fmtNum(l.qty)}</td>
                      <td>{l.unit}</td>
                      <td className="right">{fmtUSD2(l.unit_price)}</td>
                      <td className="right">{fmtUSD(l.cost)}</td>
                      <td className="muted" style={{ fontSize: 11 }}>{l.notes || ""}</td>
                    </tr>
                  );
                })}
                <tr><td colSpan={5} style={{ textAlign: "right", fontWeight: 600 }}>Subtotal</td><td className="right" style={{ fontWeight: 600 }}>{fmtUSD(subtotal)}</td><td /></tr>
              </tbody>
            </table>
          </div>
        );
      })}

      {data.adder_items.length > 0 && (
        <div>
          <h2>Adders (% of direct)</h2>
          <table>
            <thead><tr><th>Adder</th><th>Source</th><th className="right">Rate</th><th className="right">Cost</th></tr></thead>
            <tbody>
              {data.adder_items.map((l, i) => (
                <tr key={i}>
                  <td>{l.sku}</td>
                  <td><span className="badge" style={{ background: SOURCE_BADGE.catalog.bg, color: SOURCE_BADGE.catalog.fg }}>Rate Card</span></td>
                  <td className="right">{(l.unit_price * 100).toFixed(1)}%</td>
                  <td className="right">{fmtUSD(l.cost)}</td>
                </tr>
              ))}
              <tr className="total-row"><td colSpan={3} style={{ textAlign: "right" }}>Grand total (incl. adders)</td><td className="right">{fmtUSD(data.totals.grand_total)}</td></tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="muted" style={{ marginTop: 32, fontSize: 10, lineHeight: 1.5 }}>
        Quantities derived from segments + nodes in this plan. Unit prices resolved through the BEAD pricing waterfall:
        manual override on the Costs page → weighted average across all loaded BEAD projects → cost_catalog fallback.
        Lines flagged <span className="badge" style={{ background: SOURCE_BADGE.missing.bg, color: SOURCE_BADGE.missing.fg }}>No price</span> need
        a unit price entered on the Costs page. Lines marked <span className="badge" style={{ background: SOURCE_BADGE.catalog.bg, color: SOURCE_BADGE.catalog.fg }}>Catalog</span>
        come from the legacy cost_catalog and should be replaced with vendor-quoted overrides as actuals come in.
      </div>
    </div>
  );
}
